import { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { kvGet, kvSet } from '@/lib/db';
import { useSyncStore } from '@/stores/syncStore';
import { Panel } from '@/components/ui/Panel';
import { Button } from '@/components/ui/Button';
import { StatTile } from '@/components/ui/StatTile';
import { StatusChip } from '@/components/ui/StatusChip';
import { asRag, ragTone } from '@/components/ui/statusTone';
import { date, dateTime, isoDate, num, pct, tonnes } from '@/lib/format';
import type { DashboardSummary } from '@/types/models';
import { DecisionList } from './dashboard/DecisionList';
import { AllOperations } from './dashboard/AllOperations';
import { Greeting, Headline } from './dashboard/Headline';
import { ExportBar } from '@/components/ui/ExportBar';
import { figures, type Sheet } from '@/lib/export';

// Charts are ~105 KB gzipped. The tiles answer the morning questions on their
// own, so the trend panels arrive in their own chunk a moment later rather
// than holding up the first paint on a tablet.
const Trends = lazy(() => import('./dashboard/Trends').then((m) => ({ default: m.Trends })));

const CACHE_KEY = 'dashboard.summary';

const RANGES = [
  { days: 14, label: '14 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
];

/** One RPC fills every panel - the client never assembles KPIs itself.
 *  When offline we show the last cached payload, clearly stamped, so a
 *  cached number is never mistaken for a live one. */
export function Dashboard() {
  const online = useSyncStore((s) => s.online);
  const [day, setDay] = useState(() => isoDate());
  const [days, setDays] = useState(30);
  const [data, setData] = useState<DashboardSummary | null>(null);
  const [stale, setStale] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const maxDay = isoDate();
  // Only today's payload is cached for offline use. A back-dated view is a
  // deliberate look at history and belongs online, where it is definitive.
  const cacheKey = `${CACHE_KEY}.${day}`;

  const from = useMemo(() => {
    const d = new Date(`${day}T00:00:00`);
    d.setDate(d.getDate() - days);
    return isoDate(d);
  }, [day, days]);

  function shiftDay(delta: number) {
    const d = new Date(`${day}T00:00:00`);
    d.setDate(d.getDate() + delta);
    const next = isoDate(d);
    if (next <= maxDay) setDay(next);
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    (async () => {
      const cached = await kvGet<DashboardSummary>(cacheKey);
      if (cached && !cancelled) {
        setData(cached);
        setStale(true);
        setLoading(false);
      }

      if (!navigator.onLine) {
        setLoading(false);
        return;
      }

      const { data: fresh, error: err } = await supabase.rpc('dashboard_summary', { p_date: day });
      if (cancelled) return;

      if (err) {
        setError(err.message);
      } else if (fresh) {
        setData(fresh as DashboardSummary);
        setStale(false);
        setError(null);
        await kvSet(cacheKey, fresh);
      }
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [online, day, cacheKey]);

  if (loading && !data) {
    return (
      <div className="centered">
        <span className="spinner" />
        <span className="small">Loading dashboard…</span>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="placeholder">
        <h2>No dashboard data yet</h2>
        <p className="muted">
          {error ?? 'Capture a daily diary and the dashboard will populate from it.'}
        </p>
      </div>
    );
  }

  const { today, fleet, production, blast, maintenance, parts } = data;

  // The workbook: every tile, the machines down and the trend's days.
  async function sheets(): Promise<Sheet[]> {
    const d = data!;
    const out: Sheet[] = [
      { name: 'Summary', rows: figures([
        ['Date', d.as_at],
        ['Production', d.today.tonnes_production, 't'], ['Target', d.today.target_tonnes, 't'],
        ['Achievement', d.today.achievement_pct, '%'], ['ADT loads', d.today.loads],
        ['Estimated', d.today.tonnes_estimated, 't'], ['Weighbridge', d.today.tonnes_actual, 't'],
        ['Operating hours', d.today.operating_hours, 'h'], ['Breakdown hours', d.today.breakdown_hours, 'h'],
        ['Weather hours', d.today.weather_hours, 'h'],
        ['Availability', d.fleet.availability_pct, '%'], ['Availability MTD', d.fleet.availability_mtd, '%'],
        ['Utilisation (diaries)', d.fleet.utilisation_pct, '%'], ['Utilisation MTD (diaries)', d.fleet.utilisation_mtd, '%'],
        ['Plant utilisation', d.fleet.plant_utilisation_pct, '%'], ['Plant utilisation MTD', d.fleet.plant_utilisation_mtd, '%'],
        ['Fleet utilisation', d.fleet.vehicle_utilisation_pct, '%'], ['Fleet utilisation MTD', d.fleet.vehicle_utilisation_mtd, '%'],
        ['Km driven MTD', d.fleet.distance_km_mtd, 'km'], ['Utilisation measured to', d.fleet.utilisation_measured_to],
        ['Machines up', d.fleet.machines_available], ['Machines total', d.fleet.machines_total],
        ['Machines down', d.fleet.machines_down],
        ['Week to date', d.production.tonnes_wtd, 't'], ['Month to date', d.production.tonnes_mtd, 't'],
        ['Target to date', d.production.target_mtd, 't'], ['Month target', d.production.target_month, 't'],
        ['Achievement MTD', d.production.achievement_mtd_pct, '%'], ['Year to date', d.production.tonnes_ytd, 't'],
        ['Average per day', d.production.avg_tonnes_per_day, 't'], ['Actual payload', d.production.actual_payload_t, 't'],
        ['Loads MTD', d.production.loads_mtd],
        ['Current blast', d.blast.current_blast_no], ['Blast remaining', d.blast.remaining_tonnes, 't'],
        ['Blast consumed', d.blast.consumed_tonnes, 't'], ['Average consumption', d.blast.avg_daily_consumption, 't/day'],
        ['Production days remaining', d.blast.production_days_remaining, 'days'],
        ['Expected depletion', d.blast.expected_depletion_date], ['Blast status', d.blast.status_colour],
        ['Open breakdowns', d.maintenance.open_breakdowns], ['Critical breakdowns', d.maintenance.critical_breakdowns],
        ['Services due', d.maintenance.services_due], ['Services overdue', d.maintenance.services_overdue],
        ['Breakdown hours MTD', d.maintenance.breakdown_hours_mtd, 'h'], ['Weather hours MTD', d.maintenance.weather_hours_mtd, 'h'],
        ['Critical parts short', d.parts.critical_parts_short], ['Parts below minimum', d.parts.parts_below_minimum],
        ['Parts on order', d.parts.parts_on_order], ['Parts delaying repairs', d.parts.parts_delaying_repairs],
      ]) },
      { name: 'Machines down', rows: d.machines_down_detail.map((m) => ({
        Machine: m.fleet_no, Fault: m.fault ?? '', 'Hours down': m.hours_down ?? '', Status: m.status ?? '',
        'Waiting for': m.blocking_parts ?? '', ETA: m.eta ?? '',
      })) },
    ];
    const { data: rows } = await supabase.from('v_production_daily')
      .select('production_date,tonnes_production,target_tonnes,achievement_pct,loads,availability_pct,utilisation_pct,breakdown_hours,weather_hours')
      .gte('production_date', from).lte('production_date', day).order('production_date');
    const cached = rows ?? (await kvGet<Record<string, unknown>[]>(`view.v_production_daily.${JSON.stringify([from, day])}`)) ?? [];
    out.push({ name: `Daily (${days} days)`, rows: (cached as Record<string, number | string | null>[]).map((r) => ({
      Date: r.production_date, Tonnes: r.tonnes_production, Target: r.target_tonnes, 'Achievement %': r.achievement_pct,
      Loads: r.loads, 'Availability %': r.availability_pct, 'Utilisation %': r.utilisation_pct,
      'Breakdown h': r.breakdown_hours, 'Weather h': r.weather_hours,
    })) });
    return out;
  }

  const isToday = day === maxDay;

  return (
    <div className="col" style={{ gap: 'var(--sp-4)' }}>
      <div className="row date-nav" style={{ flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <Greeting data={data} isToday={isToday} />
        <span className="spacer" />
        <div className="dash-controls">
        <Button variant="secondary" onClick={() => shiftDay(-1)} aria-label="Previous day">←</Button>
        <input
          type="date"
          value={day}
          max={maxDay}
          aria-label="Dashboard date"
          onChange={(e) => e.target.value && setDay(e.target.value)}
        />
        <Button variant="secondary" onClick={() => shiftDay(1)}
          disabled={day >= maxDay} aria-label="Next day">→</Button>
        {day !== maxDay && (
          <Button variant="secondary" onClick={() => setDay(maxDay)}>Today</Button>
        )}
        <div className="seg-range" role="group" aria-label="Trend period">
          {RANGES.map((r) => (
            <button key={r.days} type="button" className={`seg-range__btn${days === r.days ? ' is-on' : ''}`}
              aria-pressed={days === r.days} onClick={() => setDays(r.days)}>{r.label}</button>
          ))}
        </div>
        <ExportBar title="Quarry dashboard" subtitle={`${date(data.as_at)} · trends over ${days} days`}
          file={`quarry-dashboard-${day}`} sheets={sheets} />
        </div>
      </div>

      {stale && (
        <div className="row small" style={{ color: 'var(--rag-amber)' }}>
          <StatusChip tone="amber">Cached</StatusChip>
          <span>
            Showing the last synced figures — as at{' '}
            {dateTime(data.generated_at)}
          </span>
        </div>
      )}

      <DecisionList data={data} />

      <Headline data={data} isToday={isToday} />

      {/* Every operation (quarry, concrete, brick, pre-cast): cards, then per site */}
      <AllOperations to={day} cards isToday={isToday} title="Production by operation and site" />

      <Suspense fallback={
        <Panel title="Trends">
          <div className="centered" style={{ padding: 'var(--sp-6)' }}><span className="spinner" /></div>
        </Panel>
      }>
        <Trends from={from} to={day} />
      </Suspense>

      {/* A - Today */}
      <Panel title={`${isToday ? 'Today' : 'Day'} · ${date(data.as_at)}`}>
        <div className="grid grid--stats">
          <StatTile label="Production" value={today.tonnes_production} unit="t" large
            rag={today.achievement_pct == null ? 'grey'
              : today.achievement_pct >= 95 ? 'green'
              : today.achievement_pct >= 80 ? 'amber' : 'red'} />
          <StatTile label="Target" value={today.target_tonnes} unit="t" />
          <StatTile label="Achievement" value={today.achievement_pct} unit="%" decimals={1} />
          <StatTile label="ADT loads" value={today.loads} />
          <StatTile label="Estimated" value={today.tonnes_estimated} unit="t" />
          <StatTile label="Weighbridge" value={today.tonnes_actual} unit="t" />
          <StatTile label="Operating hrs" value={today.operating_hours} decimals={1} />
          <StatTile label="Breakdown hrs" value={today.breakdown_hours} decimals={1} />
          <StatTile label="Weather hrs" value={today.weather_hours} decimals={1} />
        </div>
      </Panel>

      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
        {/* B - Fleet */}
        <Panel title="Fleet">
          <div className="grid grid--stats">
            <StatTile label="Availability" value={fleet.availability_pct} unit="%" decimals={1}
              sub={`MTD ${pct(fleet.availability_mtd)} %`} />
            {fleet.plant_utilisation_mtd === undefined ? (
              <StatTile label="Utilisation" value={fleet.utilisation_pct} unit="%" decimals={1}
                sub={`MTD ${pct(fleet.utilisation_mtd)} %`} />
            ) : (
              <>
                {((fleet.plant_count ?? 0) > 0 || (fleet.vehicle_count ?? 0) === 0) && (
                  <StatTile label="Plant utilisation" decimals={1} unit="%"
                    value={fleet.plant_utilisation_pct ?? fleet.plant_utilisation_mtd}
                    sub={fleet.plant_utilisation_pct != null
                      ? `MTD ${pct(fleet.plant_utilisation_mtd)} %`
                      : `month to ${date(fleet.utilisation_measured_to)}`} />
                )}
                {(fleet.vehicle_count ?? 0) > 0 && (
                  <StatTile label="Fleet utilisation" decimals={1} unit="%"
                    value={fleet.vehicle_utilisation_pct ?? fleet.vehicle_utilisation_mtd}
                    sub={fleet.vehicle_utilisation_pct != null
                      ? `MTD ${pct(fleet.vehicle_utilisation_mtd)} % · ${num(fleet.distance_km ?? 0)} km`
                      : `month to ${date(fleet.utilisation_measured_to)} · ${num(fleet.distance_km_mtd ?? 0)} km`} />
                )}
              </>
            )}
            <StatTile label="Machines up" value={fleet.machines_available}
              sub={`of ${fleet.machines_total}`} rag="green" />
            <StatTile label="Machines down" value={fleet.machines_down}
              rag={fleet.machines_down > 0 ? 'red' : 'green'} />
          </div>
        </Panel>

        {/* C - Production */}
        <Panel title="Production">
          <div className="grid grid--stats">
            <StatTile label="Week to date" value={production.tonnes_wtd} unit="t" />
            <StatTile label="Month to date" value={production.tonnes_mtd} unit="t"
              sub={mtdTargetLine(production)} rag={mtdRag(production)} />
            <StatTile label="Year to date" value={production.tonnes_ytd} unit="t" />
            <StatTile label="Avg per day" value={production.avg_tonnes_per_day} unit="t" decimals={0} />
            <StatTile label="Actual payload" value={production.actual_payload_t} unit="t" decimals={1}
              sub={`assumed ${tonnes(production.assumed_payload_t ?? 30)} t`} />
            <StatTile label="Loads MTD" value={production.loads_mtd} />
          </div>
        </Panel>


        {/* D - Blast */}
        <Panel
          title="Blast stock"
          actions={<StatusChip tone={ragTone(blast.status_colour)}>
            {blast.status_colour === 'red' ? 'Blast now'
              : blast.status_colour === 'amber' ? 'Plan blast' : 'Sufficient'}
          </StatusChip>}
        >
          <div className="grid grid--stats">
            <StatTile label="Current blast" value={blast.current_blast_no ?? '—'} />
            <StatTile label="Remaining" value={blast.remaining_tonnes} unit="t"
              rag={asRag(blast.status_colour)} />
            <StatTile label="Consumed" value={blast.consumed_tonnes} unit="t"
              sub={blast.original_tonnes ? `of ${tonnes(blast.original_tonnes)} t` : undefined} />
            <StatTile label="Avg consumption" value={blast.avg_daily_consumption} unit="t/d" decimals={0} />
            <StatTile label="Days remaining" value={blast.production_days_remaining} decimals={1}
              sub="production days" />
            <StatTile label="Expected depletion" value={date(blast.expected_depletion_date)} />
          </div>
        </Panel>

        {/* E - Maintenance */}
        <Panel title="Maintenance">
          <div className="grid grid--stats">
            <StatTile label="Open breakdowns" value={maintenance.open_breakdowns}
              rag={maintenance.open_breakdowns > 0 ? 'amber' : 'green'} />
            <StatTile label="Critical" value={maintenance.critical_breakdowns}
              rag={maintenance.critical_breakdowns > 0 ? 'red' : 'green'} />
            <StatTile label="Services due" value={maintenance.services_due} rag="amber" />
            <StatTile label="Services overdue" value={maintenance.services_overdue}
              rag={maintenance.services_overdue > 0 ? 'red' : 'green'} />
            <StatTile label="Breakdown hrs MTD" value={maintenance.breakdown_hours_mtd} decimals={1} />
            <StatTile label="Weather hrs MTD" value={maintenance.weather_hours_mtd} decimals={1} />
          </div>
        </Panel>

        {/* F - Parts */}
        <Panel title="Parts">
          <div className="grid grid--stats">
            <StatTile label="Critical short" value={parts.critical_parts_short}
              rag={parts.critical_parts_short > 0 ? 'red' : 'green'} />
            <StatTile label="Below minimum" value={parts.parts_below_minimum} rag="amber" />
            <StatTile label="On order" value={parts.parts_on_order} />
            <StatTile label="Delaying repairs" value={parts.parts_delaying_repairs}
              rag={parts.parts_delaying_repairs > 0 ? 'red' : 'green'} />
          </div>
        </Panel>
      </div>

      {/* Machines down detail */}
      {data.machines_down_detail.length > 0 && (
        <Panel title="Machines down">
          <div className="col">
            {data.machines_down_detail.map((m) => (
              <div key={m.fleet_no} className="row" style={{ flexWrap: 'wrap' }}>
                <StatusChip tone="red">{m.fleet_no}</StatusChip>
                <span>{m.fault ?? 'Fault not recorded'}</span>
                <span className="muted small">
                  {m.hours_down != null ? `${m.hours_down} h down` : ''}
                  {m.status ? ` · ${m.status.replace(/_/g, ' ')}` : ''}
                </span>
                {m.blocking_parts && (
                  <span className="small" style={{ color: 'var(--rag-amber)' }}>
                    {m.blocking_parts}
                    {m.eta ? ` · ETA ${m.eta}` : ' · no ETA'}
                  </span>
                )}
              </div>
            ))}
          </div>
        </Panel>
      )}
    </div>
  );
}

/**
 * What the month-to-date tile says under the tonnes. Pro rata compares
 * with the share of the month's target due by today; whole-month compares
 * with all of it (Administration → Settings → Month-to-date target).
 */
function mtdTargetLine(p: DashboardSummary['production']): string | undefined {
  if (!p.target_mtd) return undefined;
  const ach = p.achievement_mtd_pct == null ? '' : ` · ${pct(p.achievement_mtd_pct)} %`;
  if (p.target_basis === 'full_month') return `of month target ${tonnes(p.target_mtd)} t${ach}`;
  const days = p.working_days && p.working_days_elapsed != null
    ? ` (day ${p.working_days_elapsed} of ${p.working_days})` : '';
  return `target to date ${tonnes(p.target_mtd)} t${ach}${days}`;
}

/** Green / amber / red only against a pro-rata target; against the whole
 *  month every day but the last would read red. */
function mtdRag(p: DashboardSummary['production']): 'green' | 'amber' | 'red' | undefined {
  if (p.target_basis !== 'pro_rata' || p.achievement_mtd_pct == null) return undefined;
  return p.achievement_mtd_pct >= 95 ? 'green' : p.achievement_mtd_pct >= 80 ? 'amber' : 'red';
}
